import { useCallback, useEffect, useMemo } from "react";

import { Values } from "src/modules/SubmissionPeriods/validation";
import { useUpdateSubmissionPeriodMutation } from "src/services/state-submission";
import SubmissionPeriod from "src/types/SubmissionPeriod";
import { isApiError } from "src/utils/apiError";
import { toastError, toastSuccess } from "src/utils/toast";

export const useInitialValues = (
  submissionPeriod: SubmissionPeriod
): Values => {
  const { name, submissionType, dueDate, lockedDate, overdueSetting, active } =
    submissionPeriod;

  const dueDateIso = new Date(dueDate);
  const formattedDueDate = dueDateIso.toISOString().split("T")[0];

  const lockedDateIso = lockedDate ? new Date(lockedDate) : "";
  const formattedLockedDate = lockedDateIso
    ? lockedDateIso.toISOString().split("T")[0]
    : "";

  return useMemo(() => {
    const values: Values = {
      name,
      submissionType,
      dueDate: formattedDueDate,
      lockedDate: formattedLockedDate,
      overdueSetting: overdueSetting ?? "submissionDate",
      active,
    };

    return values;
  }, []);
};

export const useUpdateFormSubmissionPeriod: (
  submissionPeriodId: number,
  onClose: () => void
) => {
  errors: string[] | undefined;
  onSubmit: (values: Values) => Promise<void>;
} = (submissionPeriodId, onClose) => {
  const [
    trigger,
    { data, error, isError, isLoading, isSuccess, isUninitialized },
  ] = useUpdateSubmissionPeriodMutation();
  useEffect(() => {
    if (!isUninitialized && !isLoading && isSuccess) {
      toastSuccess("Submission Period has been updated");
    }
  }, [data, isError, isLoading, isSuccess, isUninitialized]);

  const onSubmit = useCallback(
    async (values: Values) => {
      const {
        name,
        submissionType,
        dueDate,
        lockedDate,
        overdueSetting,
        active,
      } = values;

      const endOfDayDueDate = new Date(dueDate)
      endOfDayDueDate.setUTCHours(23, 59, 59, 0);

      const endOfDateLockedDate = lockedDate ? new Date(lockedDate) : undefined;
      endOfDateLockedDate?.setUTCHours(23, 59, 59, 0);

      const submissionPeriod = {
        lockedDate: endOfDateLockedDate ? endOfDateLockedDate.toISOString() : null,
        dueDate: endOfDayDueDate.toISOString(),
        active,
        name,
        submissionType,
        overdueSetting,
      };

      try {
        await trigger({ submissionPeriod, submissionPeriodId }).unwrap();
        onClose();
      } catch (error) {
        if (error !== null && typeof error === "object" && "data" in error) {
          const data = error.data;
          if (
            data !== null &&
            typeof data === "object" &&
            "consumerErrorMessage" in data
          ) {
            const errorMessage = data.consumerErrorMessage as string;
            if (errorMessage.includes("BadRequestException")) {
              toastError(errorMessage.replace("BadRequestException: ", ""));
              return;
            }
          }
        }
        toastError(
          "An error occurred. Submission Period could not be updated."
        );
      }
    },
    [trigger]
  );

  const errors = useMemo(() => {
    if (!error || !isError) {
      return undefined;
    }

    if (isApiError(error)) {
      return [error.data?.error];
    }

    return ["Please try again or contact support@120water.com"];
  }, [error, isError]);

  return useMemo(() => ({ errors, onSubmit }), [errors, onSubmit]);
};
